import React from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "react-query";

import { useRouter } from "next/router";
import { QUERY_KEYS, USER_TYPES } from "../../constants";
import api from "../../api";

const missingUserProvider = "You forgot to wrap your app in <UserProvider>";

export const UserContext = React.createContext({
  get user() {
    throw new Error(missingUserProvider);
  },
  get error() {
    throw new Error(missingUserProvider);
  },
  get isLoading() {
    throw new Error(missingUserProvider);
  },
});
UserContext.displayName = "UserContext";

export const useUser = () => React.useContext(UserContext);

const UserProvider = ({ children }) => {
  const client = api();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { data: user, isLoading: isGettingUser } = useQuery(
    QUERY_KEYS.USER,
    client.getUser,
    {
      refetchOnWindowFocus: true,
      select: (undecoratedUser) => ({
        ...undecoratedUser,
        isAnonymous:
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS.id ||
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS_UNIVERSE.id,
        isFamily: undecoratedUser.userTypeId === USER_TYPES.FAMILY.id,
        isUnregisteredFamily:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_FAMILY.id,
        isUnregisteredTeacher:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_TEACHER.id,
        isClassroomTeacher:
          undecoratedUser.userTypeId === USER_TYPES.CLASSROOM_TEACHER.id,
        isUserRegistrationEnabled: !!undecoratedUser.features?.registration,
        isLoggedIn: [
          USER_TYPES.FAMILY.id,
          USER_TYPES.CLASSROOM_TEACHER.id,
        ].includes(undecoratedUser.userTypeId),
      }),
    },
  );

  const {
    mutate: updateUser,
    isLoading: isUpdatingUser,
    error: updateError,
  } = useMutation((newUserProperties) => client.updateUser(newUserProperties), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.USER);
    },
  });

  const { mutate: logout } = useMutation(() => client.logout(), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.USER);
      router.replace("/");
    },
  });

  const isLoading = [isGettingUser, isUpdatingUser].includes(true);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        updateUser,
        errors: { updateError },
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

UserProvider.defaultProps = {
  children: undefined,
};

export default UserProvider;
